import axios from "axios";
import i18next from "i18next";

const AXIOS_STATUS_MESSAGE = /^Request failed with status code \d+$/i;
const NETWORK_ERROR_MESSAGE = /^Network Error$/i;

export const apiFieldErrors = (error: unknown): Record<string, string> => {
    const original =
        error instanceof Error && error.cause ? error.cause : error;
    if (!axios.isAxiosError<{ errors?: Record<string, string[]> }>(original)) {
        return {};
    }
    return Object.fromEntries(
        Object.entries(original.response?.data?.errors || {}).map(
            ([field, messages]) => [field.split(".")[0], messages[0]]
        )
    );
};

const isGenericTransportMessage = (message: string): boolean =>
    AXIOS_STATUS_MESSAGE.test(message) || NETWORK_ERROR_MESSAGE.test(message);

const translateStatusFallback = (status?: number): string => {
    switch (status) {
        case 404:
            return i18next.t("file_viewer.load_error_not_found", {
                ns: "global",
            });
        case 403:
            return i18next.t("file_viewer.load_error_forbidden", {
                ns: "global",
            });
        case 401:
            return i18next.t("errors.unauthorized", { ns: "global" });
        case 500:
        case 502:
        case 503:
            return i18next.t("errors.server", { ns: "global" });
        default:
            return i18next.t("file_viewer.load_error", { ns: "global" });
    }
};

const readMessageFromPayload = (payload: unknown): string | null => {
    if (!payload || typeof payload !== "object") {
        return null;
    }

    if (payload instanceof Blob) {
        return null;
    }

    const record = payload as Record<string, unknown>;
    if (record.errors && typeof record.errors === "object") {
        const values = Object.values(record.errors as Record<string, unknown>)
            .flatMap((value) => (Array.isArray(value) ? value : [value]))
            .filter(
                (value): value is string =>
                    typeof value === "string" && value.trim().length > 0
            );

        if (values.length > 0) {
            return values.join(" ");
        }
    }

    if (typeof record.message === "string" && record.message.trim()) {
        return record.message.trim();
    }

    return null;
};

export const extractApiErrorMessage = async (
    error: unknown
): Promise<string> => {
    if (!axios.isAxiosError(error)) {
        if (
            error instanceof Error &&
            !isGenericTransportMessage(error.message)
        ) {
            return error.message;
        }

        return i18next.t("file_viewer.load_error", { ns: "global" });
    }

    const response = error.response;
    let message = readMessageFromPayload(response?.data);

    if (!message && response?.data instanceof Blob) {
        try {
            const text = await response.data.text();
            const json = JSON.parse(text) as unknown;
            message = readMessageFromPayload(json);
        } catch {
            // ignore malformed blob error payloads
        }
    }

    if (message && !isGenericTransportMessage(message)) {
        if (i18next.exists(message, { ns: "global" })) {
            return i18next.t(message, { ns: "global" });
        }
        if (message.startsWith("api.")) {
            const strippedKey = message.replace(/^api\./, "");
            if (i18next.exists(strippedKey, { ns: "global" })) {
                return i18next.t(strippedKey, { ns: "global" });
            }
        }
        return message;
    }

    if (!error.response) {
        return i18next.t("errors.network", { ns: "global" });
    }

    return translateStatusFallback(response?.status);
};

export const isJsonErrorBlob = async (
    blob: Blob
): Promise<{ message: string } | null> => {
    if (!blob.type.includes("json") && blob.size > 4096) {
        return null;
    }

    try {
        const text = await blob.text();
        const json = JSON.parse(text) as Record<string, unknown>;
        if (typeof json.message === "string" && json.message.trim()) {
            return { message: json.message.trim() };
        }
    } catch {
        return null;
    }

    return null;
};
